import { useSyncExternalStore } from "react";

/** 主题：跟随系统（默认）或手动 light / dark；写入 <html data-theme> */
export type ThemePref = "system" | "light" | "dark";
const KEY = "as.theme";

type Listener = () => void;
const listeners = new Set<Listener>();
let pref: ThemePref = readPref();

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* ignore */
  }
  return "system";
}

function systemDark(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveTheme(p: ThemePref, sysDark = systemDark()): "light" | "dark" {
  return p === "system" ? (sysDark ? "dark" : "light") : p;
}

export function applyTheme() {
  if (typeof document === "undefined") return;
  const t = resolveTheme(pref);
  document.documentElement.dataset.theme = t;
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute("content", t === "dark" ? "#0f1115" : "#f6f7f9");
}

export function setThemePref(p: ThemePref) {
  pref = p;
  try {
    localStorage.setItem(KEY, p);
  } catch {
    /* ignore */
  }
  applyTheme();
  for (const l of listeners) l();
}

export function getThemePref(): ThemePref {
  return pref;
}

let mqBound = false;
export function initTheme() {
  applyTheme();
  if (mqBound || typeof matchMedia !== "function") return;
  mqBound = true;
  matchMedia("(prefers-color-scheme: dark)").addEventListener?.("change", () => {
    if (pref === "system") {
      applyTheme();
      for (const l of listeners) l();
    }
  });
}

export function useThemePref(): [ThemePref, (p: ThemePref) => void] {
  const v = useSyncExternalStore(
    (cb) => (listeners.add(cb), () => listeners.delete(cb)),
    () => pref,
    () => pref,
  );
  return [v, setThemePref];
}
